import { supabase } from './supabase';

type PublicAnalyticsEvent =
  | 'page_view'
  | 'cta_click'
  | 'whatsapp_click'
  | 'produto_click'
  | 'loja_search'
  | 'cassems_add_to_cart'
  | 'cassems_checkout_view'
  | 'cassems_order_submitted'
  | 'cinta_view'
  | 'cinta_cta_click'
  | 'cinta_whatsapp_click'
  | 'cinta_cart_step_1'
  | 'cinta_cart_step_2'
  | 'cinta_cart_step_3'
  | 'cinta_checkout_redirect'
  | 'cinta_payment_return'
  | 'circuito_view'
  | 'circuito_whatsapp_click';

const SESSION_KEY = 'lunins_public_session_id_v1';
const SEARCH_DEBOUNCE_KEY = 'lunins_public_last_search_v1';

function isEnabled(): boolean {
  return Boolean(
    import.meta.env.VITE_SUPABASE_URL &&
      import.meta.env.VITE_SUPABASE_ANON_KEY &&
      import.meta.env.VITE_PUBLIC_ANALYTICS_ENABLED !== '0'
  );
}

function safeUuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function getPublicSessionId(): string {
  if (typeof window === 'undefined') return 'server';
  const existing = window.localStorage.getItem(SESSION_KEY);
  if (existing) return existing;
  const created = safeUuid();
  window.localStorage.setItem(SESSION_KEY, created);
  return created;
}

function currentCampaign(path: string): string {
  if (path.startsWith('/cinta')) return 'cinta';
  if (path.startsWith('/cassems')) return 'cassems';
  if (path.startsWith('/fechamento')) return 'fechamento';
  return 'storefront';
}

function currentUtm() {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
    utm_content: params.get('utm_content') || undefined,
    utm_term: params.get('utm_term') || undefined,
  };
}

export function trackPublicEvent(eventName: PublicAnalyticsEvent, metadata: Record<string, unknown> = {}, path?: string): void {
  if (!isEnabled() || typeof window === 'undefined') return;

  const effectivePath = path || `${window.location.pathname}${window.location.search}`;
  const body = {
    session_id: getPublicSessionId(),
    event_name: eventName,
    path: effectivePath,
    page_title: document.title || undefined,
    referrer: document.referrer || undefined,
    campaign: currentCampaign(window.location.pathname),
    metadata,
    ...currentUtm(),
  };

  void supabase.functions.invoke('public-site-analytics', { body }).catch(() => {
    // Analytics nunca deve bloquear a UX do cliente.
  });
}

export function trackPublicPageView(pathname: string): void {
  trackPublicEvent('page_view', {}, pathname);
}

export function trackLojaSearch(term: string): void {
  if (typeof window === 'undefined') return;
  const normalized = term.trim().toLowerCase();
  if (normalized.length < 3) return;
  const previous = window.sessionStorage.getItem(SEARCH_DEBOUNCE_KEY);
  if (previous === normalized) return;
  window.sessionStorage.setItem(SEARCH_DEBOUNCE_KEY, normalized);
  trackPublicEvent('loja_search', { termo: normalized });
}
